import React, { useState, forwardRef } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Box,
  Typography,
  Checkbox,
  ListItemText,
  OutlinedInput,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExpandMore, Check, Clear } from '@mui/icons-material';
import { brandColors, designTokens } from '~/presentation/themes/modernTheme';

const StyledFormControl = styled(FormControl)(({ theme, size, error, success }) => {
  const sizeStyles = {
    small: {
      '& .MuiInputBase-root': {
        minHeight: 36,
        fontSize: '0.875rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
      },
    },
    medium: {
      '& .MuiInputBase-root': {
        minHeight: 44,
        fontSize: '1rem',
      },
    },
    large: {
      '& .MuiInputBase-root': {
        minHeight: 52,
        fontSize: '1rem',
      },
    },
  };

  return {
    '& .MuiInputLabel-root': {
      fontFamily: designTokens.typography.fontFamily,
      fontWeight: designTokens.typography.fontWeights.medium,
      color: brandColors.neutral[600],
      '&.Mui-focused': {
        color: success ? brandColors.success[500] : error ? brandColors.error[500] : brandColors.primary[500],
      },
    },
    
    '& .MuiOutlinedInput-root': {
      borderRadius: designTokens.borderRadius.md,
      backgroundColor: '#ffffff',
      fontFamily: designTokens.typography.fontFamily,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      
      '& fieldset': {
        borderColor: brandColors.neutral[300],
        borderWidth: 1,
      },
      
      '&:hover fieldset': {
        borderColor: success ? brandColors.success[300] : error ? brandColors.error[300] : brandColors.primary[300],
      },
      
      '&.Mui-focused fieldset': {
        borderColor: success ? brandColors.success[500] : error ? brandColors.error[500] : brandColors.primary[500],
        borderWidth: 2,
        boxShadow: `0 0 0 3px ${success ? brandColors.success[500] : error ? brandColors.error[500] : brandColors.primary[500]}20`,
      },
      
      '&.Mui-error fieldset': {
        borderColor: brandColors.error[500],
      },
      
      '&.Mui-disabled': {
        backgroundColor: brandColors.neutral[100],
        '& fieldset': {
          borderColor: brandColors.neutral[200],
        },
      },
    },
    
    '& .MuiSelect-select': {
      padding: `${designTokens.spacing.md}px ${designTokens.spacing.md}px`,
      display: 'flex',
      alignItems: 'center',
      minHeight: 'auto !important',
    },
    
    '& .MuiFormHelperText-root': {
      fontFamily: designTokens.typography.fontFamily,
      fontSize: '0.75rem',
      marginTop: designTokens.spacing.xs,
      marginLeft: 0,
      '&.Mui-error': {
        color: brandColors.error[500],
      },
    },
    
    ...sizeStyles[size || 'medium'],
  };
});

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-icon': {
    color: brandColors.neutral[400],
    transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  
  '&.Mui-focused .MuiSelect-icon': {
    color: brandColors.primary[500],
    transform: 'rotate(180deg)',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontFamily: designTokens.typography.fontFamily,
  fontSize: '0.875rem',
  padding: `${designTokens.spacing.sm}px ${designTokens.spacing.md}px`,
  margin: `0 ${designTokens.spacing.xs}px`,
  borderRadius: designTokens.borderRadius.sm,
  transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    backgroundColor: brandColors.primary[50],
    color: brandColors.primary[700],
  },
  
  '&.Mui-selected': {
    backgroundColor: brandColors.primary[100],
    color: brandColors.primary[700],
    fontWeight: designTokens.typography.fontWeights.medium,
    
    '&:hover': {
      backgroundColor: brandColors.primary[150],
    },
  },
  
  '&.Mui-disabled': {
    color: brandColors.neutral[400],
    backgroundColor: 'transparent',
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  height: 24,
  fontSize: '0.75rem',
  fontFamily: designTokens.typography.fontFamily,
  backgroundColor: brandColors.primary[100],
  color: brandColors.primary[700],
  border: `1px solid ${brandColors.primary[200]}`,
  
  '& .MuiChip-deleteIcon': {
    color: brandColors.primary[500],
    fontSize: '1rem',
    '&:hover': {
      color: brandColors.primary[700],
    },
  },
}));

const ModernSelect = forwardRef(({
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  onFocus,
  options = [],
  error,
  success,
  helperText,
  disabled = false,
  required = false,
  fullWidth = true,
  size = 'medium',
  multiple = false,
  clearable = false,
  loading = false,
  renderValue,
  getOptionLabel = (option) => option?.label || option?.name || option,
  getOptionValue = (option) => option?.value || option?.id || option,
  groupBy,
  className,
  sx,
  MenuProps,
  ...props
}, ref) => {
  const [open, setOpen] = useState(false);
  
  const handleChange = (event) => {
    if (onChange) {
      onChange(event);
    }
  };
  
  const handleClear = (event) => {
    event.stopPropagation();
    if (onChange) {
      onChange({ target: { value: multiple ? [] : '' } });
    }
  };
  
  const hasValue = multiple ? (Array.isArray(value) && value.length > 0) : (value !== undefined && value !== null && value !== '');
  
  const renderSelectedValue = (selected) => {
    if (renderValue) {
      return renderValue(selected);
    }
    
    if (multiple) {
      if (!Array.isArray(selected) || selected.length === 0) {
        return placeholder || '';
      }
      
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((val) => {
            const option = options.find(opt => getOptionValue(opt) === val);
            return (
              <StyledChip
                key={val}
                label={getOptionLabel(option || val)}
                size="small"
                onDelete={(e) => {
                  e.stopPropagation();
                  const newValue = selected.filter(v => v !== val);
                  if (onChange) {
                    onChange({ target: { value: newValue } });
                  }
                }}
              />
            );
          })}
        </Box>
      );
    }
    
    if (!hasValue) {
      return placeholder || '';
    }
    
    const option = options.find(opt => getOptionValue(opt) === selected);
    return getOptionLabel(option || selected);
  };
  
  const endAdornment = (clearable && hasValue && !disabled) ? (
    <InputAdornment position="end">
      <Box
        onClick={handleClear}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          borderRadius: '50%',
          cursor: 'pointer',
          color: brandColors.neutral[400],
          '&:hover': {
            color: brandColors.neutral[600],
            backgroundColor: brandColors.neutral[100],
          },
        }}
      >
        <Clear fontSize="small" />
      </Box>
    </InputAdornment>
  ) : null;
  
  const groupedOptions = groupBy ? 
    options.reduce((groups, option) => {
      const group = groupBy(option);
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(option);
      return groups;
    }, {}) : null;
  
  const renderOptions = () => {
    if (loading) {
      return (
        <StyledMenuItem disabled>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Carregando...</Typography>
          </Box>
        </StyledMenuItem>
      );
    }
    
    if (options.length === 0) {
      return (
        <StyledMenuItem disabled>
          <Typography variant="body2" color="textSecondary">
            Nenhuma opção disponível
          </Typography>
        </StyledMenuItem>
      );
    }
    
    if (groupedOptions) {
      return Object.entries(groupedOptions).map(([groupName, groupOptions]) => [
        <Typography
          key={`group-${groupName}`}
          sx={{
            px: 2,
            py: 1,
            fontSize: '0.75rem',
            fontWeight: designTokens.typography.fontWeights.semibold,
            color: brandColors.neutral[600],
            backgroundColor: brandColors.neutral[50],
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {groupName}
        </Typography>,
        ...groupOptions.map((option) => {
          const optionValue = getOptionValue(option);
          const optionLabel = getOptionLabel(option);
          const isSelected = multiple ? 
            (Array.isArray(value) && value.includes(optionValue)) :
            value === optionValue;
          
          return (
            <StyledMenuItem key={optionValue} value={optionValue}>
              {multiple && (
                <Checkbox
                  checked={isSelected}
                  size="small"
                  sx={{
                    color: brandColors.neutral[400],
                    '&.Mui-checked': {
                      color: brandColors.primary[500],
                    },
                  }}
                />
              )}
              <ListItemText
                primary={optionLabel}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.875rem',
                    fontFamily: designTokens.typography.fontFamily,
                  },
                }}
              />
              {!multiple && isSelected && (
                <Check
                  sx={{
                    color: brandColors.primary[500],
                    fontSize: '1rem',
                    ml: 1,
                  }}
                />
              )}
            </StyledMenuItem>
          );
        })
      ]).flat();
    }
    
    return options.map((option) => {
      const optionValue = getOptionValue(option);
      const optionLabel = getOptionLabel(option);
      const isSelected = multiple ? 
        (Array.isArray(value) && value.includes(optionValue)) :
        value === optionValue;
      
      return (
        <StyledMenuItem key={optionValue} value={optionValue}>
          {multiple && (
            <Checkbox
              checked={isSelected}
              size="small"
              sx={{
                color: brandColors.neutral[400],
                '&.Mui-checked': {
                  color: brandColors.primary[500],
                },
              }}
            />
          )}
          <ListItemText
            primary={optionLabel}
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '0.875rem',
                fontFamily: designTokens.typography.fontFamily,
              },
            }}
          />
          {!multiple && isSelected && (
            <Check
              sx={{
                color: brandColors.primary[500],
                fontSize: '1rem',
                ml: 1,
              }}
            />
          )}
        </StyledMenuItem>
      );
    });
  };
  
  return (
    <StyledFormControl
      ref={ref}
      fullWidth={fullWidth}
      size={size}
      error={!!error}
      success={success}
      disabled={disabled}
      className={`modern-select ${className || ''}`}
      sx={sx}
    >
      {label && (
        <InputLabel required={required}>
          {label}
        </InputLabel>
      )}
      
      <StyledSelect
        value={value || (multiple ? [] : '')}
        onChange={handleChange}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onBlur={onBlur}
        onFocus={onFocus}
        open={open}
        multiple={multiple}
        displayEmpty={!!placeholder}
        renderValue={renderSelectedValue}
        IconComponent={ExpandMore}
        input={
          <OutlinedInput
            label={label}
            endAdornment={endAdornment}
          />
        }
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: designTokens.borderRadius.lg,
              boxShadow: designTokens.shadows.lg,
              border: `1px solid ${brandColors.neutral[200]}`,
              mt: 1,
              maxHeight: 300,
              '& .MuiList-root': {
                padding: designTokens.spacing.xs,
              },
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          ...MenuProps,
        }}
        {...props}
      >
        {renderOptions()}
      </StyledSelect>
      
      {(error || helperText) && (
        <FormHelperText error={!!error}>
          {error || helperText}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
});

// Componente de Select com label customizado
export const ModernSelectWithLabel = ({
  label,
  required = false,
  description,
  children,
  error,
  className,
  sx,
  ...props
}) => {
  return (
    <Box className={`modern-select-with-label ${className || ''}`} sx={sx}>
      {label && (
        <Typography
          component="label"
          sx={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: designTokens.typography.fontWeights.medium,
            color: error ? brandColors.error[500] : brandColors.neutral[700],
            mb: 1,
            fontFamily: designTokens.typography.fontFamily,
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{
                color: brandColors.error[500],
                ml: 0.5,
              }}
            >
              *
            </Typography>
          )}
        </Typography>
      )}
      
      {description && (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: brandColors.neutral[600],
            mb: 1,
            fontFamily: designTokens.typography.fontFamily,
          }}
        >
          {description}
        </Typography>
      )}
      
      {children || <ModernSelect {...props} error={error} />}
    </Box>
  );
};

ModernSelect.displayName = 'ModernSelect';

export default ModernSelect;